import { Group } from '@mantine/core';
import { ReactElement } from 'react';
import { AgentEventLog, AgentEventTypes, AgentSource } from '../../api/events-client/events-client.type';
import { STANDARD_DATE_FORMATS } from '../../helpers/date-time-helpers';
import type { DateInput } from '../../helpers/date-time-helpers/date-time-helpers.type';
import { formatDate } from '../../helpers/date-time-helpers/formatting/formatting';
import { Text } from '../../ui-library/typography/typography';
import { AGENT_SOURCES_ICONS, AGENT_SOURCES_LABELS, AGENT_SOURCES_WORDMARKS } from './agentic-process.type';

/**
 * Renders an agent source icon/wordmark with label for display in UI
 * Shows a wordmark if available, otherwise falls back to an icon
 * Includes a separator (|) for non-first items when displaying multiple sources
 *
 * @param {AgentSource} source - The agent source to render (e.g., cursor, claude, chatgpt)
 * @param {number} index - Position index for determining separator display (0-based)
 * @param {number} [height=20] - Height in pixels for the icon/wordmark image
 * @returns {ReactElement | null} Rendered component with icon and label, or null if no assets found
 */
const agentSourcesIcon = (source: AgentSource, index: number, height = 20): ReactElement | null => {
  const wordmark = AGENT_SOURCES_WORDMARKS[source];
  const icon = AGENT_SOURCES_ICONS[source];
  const label = AGENT_SOURCES_LABELS[source];

  if (!wordmark && !icon) {
    return null;
  }
  return (
    <Group gap="xs">
      {index !== 0 && <Text> | </Text>}
      {wordmark ? (
        <img src={wordmark} alt={source} height={height} />
      ) : (
        <>
          <img src={icon} alt={source} height={height} />
          <Text size="small">{label}</Text>
        </>
      )}
    </Group>
  );
};

/**
 * Analyzes agent events to calculate key metrics and statistics
 * Processes event logs to extract session information, agent usage patterns, and activity counts
 *
 * @param {AgentEventLog[]} agentEvents - Array of agent event logs to analyze
 * @returns {Object} Metadata object containing:
 *   - uniqueSessions: Number of distinct sessions in the events
 *   - mostUsedAgentSource: Agent source with the highest event count (UNKNOWN if no events)
 *   - llmModels: Array of unique LLM models used across events
 *   - totalToolCalls: Count of tool call events
 *   - totalUserPrompts: Count of user prompt events
 */
const calculateAgentEventMetadata = (
  agentEvents: AgentEventLog[],
): {
  uniqueSessions: number;
  mostUsedAgentSource: AgentSource;
  llmModels: string[];
  totalToolCalls: number;
  totalUserPrompts: number;
} => {
  let totalToolCalls = 0;
  let totalUserPrompts = 0;
  const uniqueSessions = new Set<string>();
  const agentSources = new Map<AgentSource, number>();
  const llmModels = new Set<string>();

  agentEvents.forEach((event) => {
    uniqueSessions.add(event.session_id);
    agentSources.set(event.agent_source, (agentSources.get(event.agent_source) || 0) + 1);
    if (event.llm_model) {
      llmModels.add(event.llm_model);
    }
    if (event.event_type === AgentEventTypes.TOOL_CALL) {
      totalToolCalls++;
    } else if (event.event_type === AgentEventTypes.USER_PROMPT) {
      totalUserPrompts++;
    }
  });

  return {
    uniqueSessions: uniqueSessions.size,
    mostUsedAgentSource:
      agentSources.size > 0
        ? Array.from(agentSources.entries()).sort((a, b) => b[1] - a[1])[0][0]
        : AgentSource.UNKNOWN,
    llmModels: Array.from(llmModels),
    totalToolCalls,
    totalUserPrompts,
  };
};

/**
 * Formats a date for display in agentic process events and UI elements
 * Uses the standard format: 'MMM D, YYYY h:mm' (e.g., "Sep 11, 2024 2:30")
 *
 * @param {DateInput} dateInput - Date to format
 * @returns {string} Formatted date string
 */
const formatAgenticProcessDate = (dateInput: DateInput): string => {
  return formatDate(dateInput, { format: STANDARD_DATE_FORMATS.FULL_DATE_AND_TIME });
};

export { agentSourcesIcon, calculateAgentEventMetadata, formatAgenticProcessDate };
